import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { Link } from 'react-router-dom'

import { ApiError, api } from '../../lib/api'
import { clockTime, dayHeading, shortDateTime } from '../../lib/datetime'
import { useCategoryNames, useOperatorTimezone } from '../../lib/operator'
import type { BookingRow } from '../../lib/optypes'
import type { SlotsResponse } from '../../lib/types'

import { OperatorLayout } from './OperatorLayout'

/** A row action: 40 px tall, bordered, and unmistakably a control rather than prose. */
const ACTION =
  'inline-flex min-h-10 items-center rounded-btn border border-line bg-white px-3.5 text-sm font-semibold text-blue-deep transition-colors hover:border-blue hover:bg-blue-tint'

/** The one action that emails a homeowner. Separated by colour, not only by position. */
const DESTRUCTIVE =
  'ml-auto inline-flex min-h-10 items-center rounded-btn border border-line bg-white px-3.5 text-sm font-semibold text-blue-press transition-colors hover:border-blue-press hover:bg-sand'

/**
 * The visits calendar (01 §4), grouped by day.
 *
 * Rescheduling from here offers **only slots the system would offer a homeowner**, so Andrew
 * cannot accidentally put a visit somewhere his own rules exclude. Both actions email the
 * homeowner — a moved visit nobody was told about is worse than no visit.
 */
export function Bookings() {
  const queryClient = useQueryClient()
  const tz = useOperatorTimezone()
  const names = useCategoryNames()
  const [movingId, setMovingId] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)

  const bookings = useQuery({
    queryKey: ['op-bookings'],
    queryFn: () => api.get<BookingRow[]>('/op/bookings'),
    refetchInterval: 60_000,
  })
  const slots = useQuery({
    queryKey: ['op-slots'],
    queryFn: () => api.get<SlotsResponse>('/availability/slots'),
    enabled: movingId !== null,
  })

  const change = useMutation({
    mutationFn: ({ id, body }: { id: string; body: Record<string, unknown> }) =>
      api.patch(`/op/bookings/${id}`, body),
    onSuccess: async () => {
      setError(null)
      setMovingId(null)
      await queryClient.invalidateQueries({ queryKey: ['op-bookings'] })
      await queryClient.invalidateQueries({ queryKey: ['op-slots'] })
    },
    onError: (err) =>
      setError(
        err instanceof ApiError
          ? (err.problem.detail ?? err.problem.title)
          : 'Could not change that.',
      ),
  })

  const byDay = new Map<string, BookingRow[]>()
  for (const row of bookings.data ?? []) {
    const day = dayHeading(row.slot_start, tz)
    byDay.set(day, [...(byDay.get(day) ?? []), row])
  }

  return (
    <OperatorLayout>
      <h1 className="text-2xl">Visits</h1>
      <p className="mt-2 text-sm text-muted">
        The next 30 days. Moving or cancelling emails the homeowner.
      </p>

      {error && (
        <p role="alert" className="mt-4 rounded-btn bg-blue-tint px-3.5 py-2.5 text-sm text-ink">
          {error}
        </p>
      )}
      {bookings.isPending && <p className="mt-8 text-muted">Loading…</p>}
      {bookings.data?.length === 0 && (
        <p className="mt-8 rounded-card border border-line bg-white p-6 text-muted">
          Nothing booked.
        </p>
      )}

      <div className="mt-6 grid gap-7">
        {[...byDay.entries()].map(([day, rows]) => (
          <section key={day}>
            <h2 className="text-lg">{day}</h2>
            <ul className="m-0 mt-3 grid list-none gap-2.5 p-0">
              {rows.map((row) => {
                const time = clockTime(row.slot_start, tz)
                return (
                  <li key={row.id} className="rounded-card border border-line bg-white p-4">
                    <div className="flex flex-wrap items-baseline justify-between gap-2">
                      <span className="font-semibold text-ink">
                        {time} · {row.homeowner_name ?? 'No name'}
                      </span>
                      <span className="font-mono text-xs text-muted">{row.status}</span>
                    </div>
                    <p className="m-0 mt-1 text-sm text-body">
                      {names(row.categories)}
                      {row.town ? ` — ${row.town}` : ''}
                    </p>

                    {/* Four bare text links, 20 px tall and three millimetres apart, with
                        "Cancel visit" next to "Move" — on a phone that is a mis-tap that emails
                        a homeowner their visit is off. Real controls, and the destructive one
                        pushed to its own end of the row. */}
                    <div className="mt-3.5 flex flex-wrap items-center gap-2">
                      {row.homeowner_phone && (
                        <a href={`tel:${row.homeowner_phone}`} className={ACTION}>
                          Call
                        </a>
                      )}
                      <Link to={`/op/requests/${row.estimate_request_id}`} className={ACTION}>
                        Open
                      </Link>
                      <button
                        type="button"
                        onClick={() => setMovingId(movingId === row.id ? null : row.id)}
                        className={ACTION}
                      >
                        {movingId === row.id ? 'Cancel move' : 'Move'}
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          if (
                            window.confirm(
                              `Cancel the visit with ${row.homeowner_name ?? 'this homeowner'}? They will be emailed.`,
                            )
                          ) {
                            change.mutate({
                              id: row.id,
                              body: { status: 'cancelled', reason: 'operator cancelled' },
                            })
                          }
                        }}
                        className={DESTRUCTIVE}
                      >
                        Cancel visit
                      </button>
                    </div>

                    {movingId === row.id && (
                      <div className="mt-4 rounded-btn border border-line bg-paper p-3">
                        <p className="m-0 text-sm text-muted">
                          Only times your own availability allows are offered.
                        </p>
                        {slots.isPending && (
                          <p className="m-0 mt-2 text-sm text-muted">Loading times…</p>
                        )}
                        <ul className="m-0 mt-3 grid list-none grid-cols-2 gap-2 p-0 sm:grid-cols-4">
                          {slots.data?.slots.slice(0, 24).map((slot) => (
                            <li key={slot.start}>
                              <button
                                type="button"
                                disabled={change.isPending}
                                onClick={() =>
                                  change.mutate({ id: row.id, body: { slot_start: slot.start } })
                                }
                                className="w-full rounded-btn border border-line-2 bg-white px-2 py-2 text-xs font-semibold text-ink hover:border-blue disabled:opacity-50"
                              >
                                {shortDateTime(slot.start, tz)}
                              </button>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </li>
                )
              })}
            </ul>
          </section>
        ))}
      </div>
    </OperatorLayout>
  )
}
